import { EMPTY_ASSISTANT_MESSAGE } from '../constants';
import type {
  AiAskApiPayload,
  AskAssistantMessage,
  AskTransportMode,
} from '../types';
import { parseFilteredListFields } from './filteredList';

export function normalizeSessionId(
  obj: Record<string, unknown>
): string | undefined {
  const s = obj.sessionId ?? obj.session_id;
  if (typeof s === 'string' && s.trim()) return s.trim();
  return undefined;
}

export function payloadFromRecord(
  raw: Record<string, unknown>
): AiAskApiPayload {
  return {
    sessionId: normalizeSessionId(raw),
    answer:
      raw.answer === undefined || raw.answer === null
        ? null
        : (raw.answer as string),
    sources: Array.isArray(raw.sources) ? raw.sources : [],
    references: Array.isArray(raw.references) ? raw.references : [],
    results: Array.isArray(raw.results) ? raw.results : [],
    debug:
      raw.debug && typeof raw.debug === 'object'
        ? (raw.debug as Record<string, unknown>)
        : {},
    mode: typeof raw.mode === 'string' ? raw.mode : undefined,
  };
}

export function buildAssistantMessage(
  raw: Record<string, unknown>,
  mode: AskAssistantMessage['mode'],
  answerOverride?: string | null
): AskAssistantMessage {
  const payload = payloadFromRecord(raw);
  const filtered = parseFilteredListFields(raw);
  const messageText =
    typeof raw.message === 'string' && raw.message.trim()
      ? raw.message.trim()
      : null;
  return {
    role: 'assistant',
    mode,
    answer:
      answerOverride !== undefined
        ? answerOverride
        : payload.answer ?? messageText ?? null,
    sources: payload.sources || [],
    references: payload.references || [],
    results: payload.results || [],
    debug: payload.debug || {},
    ...filtered,
    viewResultsError: null,
  };
}

export function getAskTransportMode(): AskTransportMode {
  return 'json';
}

export function mapApiMode(
  raw: string | undefined
): AskAssistantMessage['mode'] {
  if (raw === 'search_only') return 'search_only';
  if (raw === 'error') return 'error';
  return 'autonomous';
}

export function flattenSearchOnlyHits(
  results: unknown
): Array<Record<string, unknown>> {
  const out: Array<Record<string, unknown>> = [];
  const arr = Array.isArray(results) ? results : [];
  for (const block of arr) {
    if (!block || typeof block !== 'object') continue;
    const b = block as Record<string, unknown>;
    const idx = String(b.index ?? b.indexName ?? '').toLowerCase();
    const hits = Array.isArray(b.hits) ? b.hits : [];
    for (const h of hits) {
      if (h && typeof h === 'object')
        out.push({ ...(h as Record<string, unknown>), __index: idx });
    }
  }
  return out;
}

export { EMPTY_ASSISTANT_MESSAGE };
